import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  InputLabel, FormControl, Snackbar, Alert, Typography, IconButton, Tooltip, Box, CircularProgress,
  Grid, Stack, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import GetAppIcon from '@mui/icons-material/GetApp';
import PrintIcon from '@mui/icons-material/Print';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import GradeRoundedIcon from '@mui/icons-material/GradeRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import api from '../api';
import { useNavigate } from "react-router-dom";
import { pastel } from '../theme/palette';
import PageHeader from './PageHeader';
import ImportCsvEleves from './ImportCsvEleves';

const niveaux = ['6ème', '5ème', '4ème', '3ème', '2nde', '1ère', 'Term', '2nde Pro', '1ère Pro', 'Term Pro'];

export default function ClassesTable({ token }) {
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [pronoteOpen, setPronoteOpen] = useState(false);
  const [form, setForm] = useState({ nom: '', niveau: '', annee: '' });
  const [editId, setEditId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchClasses = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/classes');
        if (!Array.isArray(res.data)) {
          setError(typeof res.data === 'object'
            ? (res.data.error || JSON.stringify(res.data))
            : 'Erreur API ou format inattendu'
          );
          setClasses([]);
        } else {
          setClasses(res.data);
        }
      } catch (err) {
        setError(
          err.response?.status === 401
            ? 'Non autorisé : vérifiez votre connexion.'
            : err.response?.data?.error || 'Erreur lors du chargement des classes.'
        );
        setClasses([]);
      }
      setLoading(false);
    };
    fetchClasses();
  }, [token]);

  const handleOpen = (classe = null) => {
    setOpen(true);
    if (classe) {
      setForm({ nom: classe.nom, niveau: classe.niveau || '', annee: classe.annee || '' });
      setEditId(classe._id);
    } else {
      setForm({ nom: '', niveau: '', annee: '' });
      setEditId(null);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ nom: '', niveau: '', annee: '' });
    setEditId(null);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const refreshClasses = async () => {
    const res = await api.get('/classes');
    setClasses(Array.isArray(res.data) ? res.data : []);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!form.nom || form.nom.trim() === '') {
      setSnack({ open: true, message: "Le nom de la classe est obligatoire.", severity: 'error' });
      setLoading(false);
      return;
    }

    const payload = { nom: form.nom };
    if (form.niveau) payload.niveau = form.niveau;
    if (form.annee) payload.annee = form.annee;

    try {
      if (editId) {
        await api.put(`/classes/${editId}`, payload);
        setSnack({ open: true, message: 'Classe modifiée.', severity: 'success' });
      } else {
        await api.post('/classes', payload);
        setSnack({ open: true, message: 'Classe ajoutée.', severity: 'success' });
      }
      handleClose();
      await refreshClasses();
    } catch (err) {
      setSnack({
        open: true,
        message: err.response?.data?.error || err.message || "Erreur lors de l'enregistrement.",
        severity: 'error'
      });
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cette classe ?')) {
      setLoading(true);
      try {
        await api.delete(`/classes/${id}`);
        await refreshClasses();
        setSnack({ open: true, message: 'Classe supprimée.', severity: 'success' });
      } catch (err) {
        setSnack({ open: true, message: "Erreur lors de la suppression.", severity: 'error' });
      }
      setLoading(false);
    }
  };

  const escapeHtml = (unsafe) => {
    return ('' + (unsafe || '')).replace(/[&<>"'`]/g, function (m) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '`': '&#96;' })[m];
    });
  };

  const exportClassCsv = async (classe) => {
    try {
      const res = await api.get(`/eleves?classe=${encodeURIComponent(classe._id)}`);
      const eleves = Array.isArray(res.data) ? res.data : [];
      const rows = [['nom', 'prenom', 'email', 'matricule']];
      eleves.forEach(e => rows.push([e.nom || '', e.prenom || '', e.email || '', e.matricule || '']));
      const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eleves_${(classe.nom || 'classe').replace(/\s+/g, '_')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setSnack({ open: true, message: `Export de ${eleves.length} élèves terminé.`, severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || 'Erreur lors de l\'export CSV', severity: 'error' });
    }
  };

  const printClassList = async (classe) => {
    try {
      const res = await api.get(`/eleves?classe=${encodeURIComponent(classe._id)}`);
      const eleves = Array.isArray(res.data) ? res.data : [];
      const rowsHtml = eleves.map(e =>
        `<tr><td style="padding:6px;border:1px solid #ddd">${escapeHtml(e.nom || '')}</td><td style="padding:6px;border:1px solid #ddd">${escapeHtml(e.prenom || '')}</td><td style="padding:6px;border:1px solid #ddd">${escapeHtml(e.email || '')}</td><td style="padding:6px;border:1px solid #ddd">${escapeHtml(e.matricule || '')}</td></tr>`
      ).join('');
      const html = `<html><head><title>Liste élèves - ${escapeHtml(classe.nom || '')}</title><style>body { font-family: Arial, sans-serif; padding: 20px; color: #222 } h2 { margin-bottom: 8px } table { border-collapse: collapse; width: 100%; margin-top: 12px; } th { text-align: left; padding:8px; background: #f7f7f7; border:1px solid #ddd } td { padding:6px; border:1px solid #ddd }</style></head><body><h2>Liste des élèves — ${escapeHtml(classe.nom || '')}</h2><p>Nombre d'élèves : ${eleves.length}</p><table><thead><tr><th>Nom</th><th>Prénom</th><th>Email</th><th>Matricule</th></tr></thead><tbody>${rowsHtml}</tbody></table><script>window.onload = function() { window.print(); }</script></body></html>`;
      const win = window.open('', '_blank', 'width=900,height=700');
      win.document.write(html);
      win.document.close();
      setSnack({ open: true, message: `Préparation de l'impression (${eleves.length} élèves).`, severity: 'info' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || 'Erreur lors de la préparation de l\'impression', severity: 'error' });
    }
  };

  if (loading) return <Box sx={{ mt: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
  if (!classes || !Array.isArray(classes)) return <Alert severity="error" sx={{ m: 4 }}>Problème de données reçues du serveur.</Alert>;

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 2, sm: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <PageHeader
        title="Mes classes"
        subtitle="Crée une classe manuellement ou importe directement une ou plusieurs classes complètes depuis un export PRONOTE."
        backTo="/prof"
        homeTo="/"
      />

      <Stack direction="column" spacing={1.5} sx={{ mb: 2.5 }}>
        <Button
          variant="contained"
          onClick={() => handleOpen()}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, minHeight: 48, px: 3 }}
        >
          Ajouter une classe
        </Button>

        <Button
          variant="contained"
          startIcon={<UploadFileRoundedIcon />}
          onClick={() => setPronoteOpen(true)}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, minHeight: 48, px: 3 }}
        >
          Importer une classe depuis PRONOTE
        </Button>
      </Stack>

      {classes.length === 0 ? (
        <Typography sx={{ m: 4, textAlign: 'center' }}>Aucune classe trouvée.</Typography>
      ) : isMobile ? (
        <Grid container spacing={2}>
          {classes.map((classe) => (
            <Grid item xs={12} key={classe._id}>
              <Paper sx={{ p: 2.5, border: '1px solid rgba(0,0,0,0.06)' }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{classe.nom}</Typography>
                    <Typography color="text.secondary">
                      {classe.niveau || 'Niveau non défini'}{classe.annee ? ` • ${classe.annee}` : ''}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button variant="contained" startIcon={<GroupsRoundedIcon />} onClick={() => navigate(`/eleves/classe/${classe._id}`)}>
                      Élèves
                    </Button>
                    <Button variant="outlined" startIcon={<GradeRoundedIcon />} onClick={() => navigate(`/suivi-eleves?classe=${classe._id}`)}>
                      Suivi
                    </Button>
                  </Stack>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button size="small" startIcon={<EditRoundedIcon />} onClick={() => handleOpen(classe)}>Modifier</Button>
                    <Button size="small" color="error" startIcon={<DeleteRoundedIcon />} onClick={() => handleDelete(classe._id)}>Supprimer</Button>
                    <Button size="small" startIcon={<GetAppIcon />} onClick={() => exportClassCsv(classe)}>CSV</Button>
                    <Button size="small" startIcon={<PrintIcon />} onClick={() => printClassList(classe)}>Imprimer</Button>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: pastel.headerBg }}>
                <TableCell>Nom</TableCell>
                <TableCell>Niveau</TableCell>
                <TableCell>Année</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Élèves</TableCell>
                <TableCell>Suivi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {classes.map(c => (
                <TableRow key={c._id} hover sx={{ '&:hover': { bgcolor: pastel.rowHover } }}>
                  <TableCell>{c.nom}</TableCell>
                  <TableCell>{c.niveau || ''}</TableCell>
                  <TableCell>{c.annee || ''}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Button size="small" onClick={() => handleOpen(c)}>Modifier</Button>
                      <Button size="small" color="error" onClick={() => handleDelete(c._id)}>Supprimer</Button>
                      <Tooltip title="Exporter la liste (CSV)">
                        <IconButton size="small" onClick={() => exportClassCsv(c)} sx={{ backgroundColor: pastel.exportBg, color: pastel.exportColor, '&:hover': { backgroundColor: '#D0E9FF' } }}>
                          <GetAppIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Imprimer la liste">
                        <IconButton size="small" onClick={() => printClassList(c)} sx={{ backgroundColor: pastel.printBg, color: pastel.printColor, '&:hover': { backgroundColor: '#FFE8CC' } }}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" onClick={() => navigate(`/eleves/classe/${c._id}`)}>
                      Voir les élèves
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="contained" onClick={() => navigate(`/suivi-eleves?classe=${c._id}`)}>
                      Ouvrir le suivi
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editId ? "Modifier la classe" : "Ajouter une classe"}</DialogTitle>
        <DialogContent>
          <form id="classeForm" onSubmit={handleSubmit}>
            <TextField label="Nom" name="nom" value={form.nom} onChange={handleChange} margin="normal" fullWidth required />
            <FormControl fullWidth margin="normal">
              <InputLabel id="niveau-label">Niveau</InputLabel>
              <Select labelId="niveau-label" label="Niveau" name="niveau" value={form.niveau} onChange={handleChange}>
                <MenuItem value="">Sélectionner</MenuItem>
                {niveaux.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Année" name="annee" value={form.annee} onChange={handleChange} margin="normal" fullWidth />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button type="submit" form="classeForm" variant="contained" sx={{ backgroundColor: pastel.primaryBg, color: pastel.primaryColor, '&:hover': { backgroundColor: '#dccdf0' } }}>Sauvegarder</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pronoteOpen} onClose={() => setPronoteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Importer une classe depuis PRONOTE</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Utilise ce bouton si ton fichier CSV contient déjà la colonne <strong>classe</strong>.
            L’application retrouvera ou créera automatiquement les classes du prof, puis importera tous les élèves du fichier.
          </Typography>
          <ImportCsvEleves
            availableClasses={classes}
            lockedImportMode="fileclasses"
            onImport={async (data) => {
              await refreshClasses();
              setPronoteOpen(false);
              setSnack({
                open: true,
                severity: 'success',
                message: `${data?.count ?? 0} élève(s) importé(s) et ${data?.classesCreated?.length ?? 0} classe(s) créée(s).`
              });
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPronoteOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}